import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listCredit, refreshCreditLive } from '../../services/epicorApi';
import './Credit.css';

const ALL_COMPANIES = ['AU', 'NZ'];
const PAGE_SIZE = 50;

const fmtMoney = (v) =>
  v === null || v === undefined
    ? '—'
    : Number(v).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const fmtTime = (t) => (t ? new Date(t).toLocaleString() : '—');

const fmtAge = (seconds) => {
  if (seconds === null || seconds === undefined) return '—';
  const s = Number(seconds);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const STATUS_FILTERS = [
  { id: '', label: 'All' },
  { id: 'can_buy', label: 'Can buy' },
  { id: 'over_limit', label: 'Over limit' },
  { id: 'on_hold', label: 'On hold' },
  { id: 'no_limit', label: 'No limit set' },
  { id: 'quarantined', label: 'Not assessable' },
  { id: 'closed', label: 'Closed' },
];

const QUARANTINE_COPY = {
  currency_mixed: 'Open documents span more than one currency',
  orders_missing_amt: 'An open order has no amount on it',
  customer_missing: 'Not found in Epicor',
};

/* A row's headline state. Quarantine wins over everything: when the figures are
   not assessable the exposure is zeroed and the limit nulled, so "can buy" or
   "over limit" would be read off placeholders. */
const chipFor = (r) => {
  if (r.account_status === 'closed') return { label: 'Closed', cls: 'none' };
  if (r.quarantine_reason) {
    return {
      label: 'Not assessable',
      cls: 'review',
      title: QUARANTINE_COPY[r.quarantine_reason] || r.quarantine_reason,
    };
  }
  if (r.credit_hold) return { label: 'On hold', cls: 'review' };
  if (r.credit_limit === null || r.credit_limit === undefined) {
    return { label: 'No limit', cls: 'none' };
  }
  if (Number(r.over_limit_amt) > 0) return { label: 'Over limit', cls: 'review' };
  return { label: 'Can buy', cls: 'linked' };
};

const CreditPage = () => {
  const navigate = useNavigate();

  const [companyFilter, setCompanyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [sort, setSort] = useState('');
  const [dir, setDir] = useState('asc');
  const [page, setPage] = useState(0);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadError, setLoadError] = useState(null);
  const [rowsLoading, setRowsLoading] = useState(true);

  // Live-refresh overlay, keyed by `${company}-${cust_num}`. Kept beside `rows`
  // rather than merged into them so a refreshed row can be marked as live
  // without pretending the snapshot query returned it that way.
  const [liveRows, setLiveRows] = useState({});
  const [unreadable, setUnreadable] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);

  const reqIdRef = useRef(0);

  const loadRows = useCallback(async () => {
    const reqId = ++reqIdRef.current;
    setRowsLoading(true);
    try {
      setLoadError(null);
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (companyFilter) params.company = companyFilter;
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      if (sort) {
        params.sort = sort;
        params.dir = dir;
      }
      const data = await listCredit(params);
      if (reqId !== reqIdRef.current) return;
      setRows(data.credit || []);
      setTotal(data.total || 0);
      // A new page or filter is a new set of rows; last read's live figures
      // do not belong to them.
      setLiveRows({});
      setUnreadable({});
      setRefreshError(null);
    } catch (e) {
      if (reqId !== reqIdRef.current) return;
      setLoadError(e.response?.data?.detail || e.message);
    } finally {
      if (reqId === reqIdRef.current) setRowsLoading(false);
    }
  }, [companyFilter, statusFilter, search, sort, dir, page]);

  useEffect(() => { loadRows(); }, [loadRows]);

  // 450ms debounce with a 2-char minimum: without the minimum the first
  // keystroke runs a full-table ILIKE across staging.
  useEffect(() => {
    const q = searchDraft.trim();
    if (q.length === 1) return undefined;
    const t = setTimeout(() => {
      setPage(0);
      setSearch(q);
    }, 450);
    return () => clearTimeout(t);
  }, [searchDraft]);

  const toggleSort = (key) => {
    setPage(0);
    if (sort === key) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(key);
      // Amounts are most useful largest-first; identifiers and dates ascending.
      setDir(key === 'cust_id' || key === 'computed_at' ? 'asc' : 'desc');
    }
  };

  /* Reads the visible page straight from Epicor. Refreshed rows stay exactly
     where they are: a row whose new figures no longer match the active filter
     or sort is left in place with a marker rather than vanishing mid-read,
     which would be disorienting and would hide the very change just fetched.
     The list re-queries on the next filter or page change. */
  const refreshVisible = async () => {
    if (rows.length === 0) return;
    setRefreshing(true);
    setRefreshError(null);
    try {
      const data = await refreshCreditLive(
        rows.map((r) => ({ company: r.company, cust_num: r.cust_num })),
      );
      // Both maps are already keyed "{company}-{cust_num}", matching keyOf.
      setLiveRows((prev) => ({ ...prev, ...(data.credit || {}) }));
      setUnreadable((prev) => ({
        ...prev,
        ...Object.fromEntries((data.missing || []).map((k) => [k, true])),
      }));
    } catch (e) {
      const status = e.response?.status;
      setRefreshError(
        status === 429
          ? `Too soon — try again in ${e.response.headers?.['retry-after'] || 10}s.`
          : e.response?.data?.detail || e.message,
      );
    } finally {
      setRefreshing(false);
    }
  };

  const keyOf = (r) => `${r.company}-${r.cust_num}`;
  const displayRows = rows.map((r) => {
    const live = liveRows[keyOf(r)];
    // Live rows carry no name -- that comes from the staging join -- so the
    // snapshot's identity fields are kept underneath.
    return live ? { ...r, ...live, _live: true } : r;
  });

  const liveCount = displayRows.filter((r) => r._live).length;
  const unreadableCount = Object.keys(unreadable).length;
  const freshness =
    liveCount === 0
      ? { label: 'Snapshot', cls: 'none' }
      : liveCount === displayRows.length
        ? { label: 'Live', cls: 'linked' }
        : { label: `${liveCount} of ${displayRows.length} live`, cls: 'review' };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const initialLoading = rowsLoading && rows.length === 0;
  const anyFilter = Boolean(companyFilter || statusFilter || search);

  const clearFilters = () => {
    setSearchDraft('');
    setSearch('');
    setCompanyFilter('');
    setStatusFilter('');
    setSort('');
    setDir('asc');
    setPage(0);
  };

  const openRow = (r) => navigate(`/customers/${r.company}/${r.cust_num}`);

  const sortIndicator = (key) => {
    if (sort !== key) return null;
    return <span className="oc-sort-dir" aria-hidden="true">{dir === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <div className="credit-page oc-scope">
      <div className="oc-header">
        <div className="oc-header-text">
          <h1 className="oc-title">Credit</h1>
          <p className="oc-blurb">
            Buy-on-account headroom for every customer synced to the store. Figures come from
            the stored snapshot so the list can be filtered and sorted; use Refresh to read the
            visible page straight from Epicor. Checkout always reads live.
          </p>
        </div>
        <button
          type="button"
          className="oc-btn oc-btn-secondary oc-header-action"
          disabled={refreshing || rows.length === 0}
          onClick={refreshVisible}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M20.5 12a8.5 8.5 0 0 1-14.6 5.9L3 15M3.5 12A8.5 8.5 0 0 1 18.1 6.1L21 9M21 4v5h-5M3 20v-5h5" />
          </svg>
          {refreshing ? 'Reading Epicor…' : 'Refresh page live'}
        </button>
      </div>

      <div className="oc-frame oc-blueprint">
        <i className="oc-corner tl" aria-hidden="true" />
        <i className="oc-corner tr" aria-hidden="true" />
        <i className="oc-corner bl" aria-hidden="true" />
        <i className="oc-corner br" aria-hidden="true" />

        <div className="oc-filterbar">
          <div className="oc-field oc-field-search">
            <label htmlFor="credit-q">Search</label>
            <input
              id="credit-q"
              className="oc-input"
              type="text"
              placeholder="Name, customer ID or CustNum"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
            />
          </div>
          <div className="oc-field">
            <span className="oc-field-label" id="credit-co-label">Company</span>
            <div className="oc-seg" role="radiogroup" aria-labelledby="credit-co-label">
              {['', ...ALL_COMPANIES].map((v) => (
                <label key={v || 'all'} className="oc-seg-opt">
                  <input
                    type="radio"
                    name="credit-co"
                    checked={companyFilter === v}
                    onChange={() => { setPage(0); setCompanyFilter(v); }}
                  />
                  <span>{v || 'All'}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="oc-field oc-field-links">
            <span className="oc-field-label">Status</span>
            <div className="oc-chips">
              {STATUS_FILTERS.map((c) => (
                <button
                  key={c.id || 'all'}
                  type="button"
                  className={`oc-chip${statusFilter === c.id ? ' on' : ''}`}
                  onClick={() => { setPage(0); setStatusFilter(c.id); }}
                  aria-pressed={statusFilter === c.id}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="oc-resultbar">
          <span className="oc-result-count">
            <strong>{total.toLocaleString()}</strong>{' '}
            <span className="oc-muted">{total === 1 ? 'customer' : 'customers'}</span>
          </span>
          <span className="oc-credit-freshness">
            <span className={`oc-linkchip ${freshness.cls}`}>{freshness.label}</span>
            {unreadableCount > 0 && (
              <span className="oc-linkchip review">{unreadableCount} could not be read</span>
            )}
          </span>
          <span className="oc-muted oc-result-note">
            {rowsLoading && rows.length > 0
              ? 'Updating…'
              : anyFilter
                ? 'Filtered view'
                : 'Sorted by last update'}
          </span>
        </div>

        {loadError && <div className="oc-error" role="alert">{loadError}</div>}
        {refreshError && <div className="oc-error" role="alert">{refreshError}</div>}

        <div className="oc-table-wrap">
          <table className="oc-table oc-credit-table">
            <caption className="sr-only">Customer credit availability</caption>
            <thead>
              <tr>
                <th scope="col">
                  <button type="button" className="oc-sort-btn" onClick={() => toggleSort('cust_id')}>
                    Customer{sortIndicator('cust_id')}
                  </button>
                </th>
                <th scope="col" className="num">Limit</th>
                <th scope="col" className="num">Invoiced</th>
                <th scope="col" className="num">Orders</th>
                <th scope="col" className="num">Held</th>
                <th scope="col" className="num">
                  <button type="button" className="oc-sort-btn" onClick={() => toggleSort('credit_total')}>
                    Credit total{sortIndicator('credit_total')}
                  </button>
                </th>
                <th scope="col" className="num">
                  <button type="button" className="oc-sort-btn" onClick={() => toggleSort('available_credit')}>
                    Available{sortIndicator('available_credit')}
                  </button>
                </th>
                <th scope="col">Status</th>
                <th scope="col">
                  <button type="button" className="oc-sort-btn" onClick={() => toggleSort('computed_at')}>
                    Updated{sortIndicator('computed_at')}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {initialLoading &&
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={`skel-${i}`} className="oc-row-skel">
                    {['70%', '50%', '50%', '50%', '40%', '55%', '55%', '45%', '45%'].map((w, j) => (
                      <td key={j}><div className="ui-skel" style={{ height: 14, width: w }} /></td>
                    ))}
                  </tr>
                ))}
              {!initialLoading && displayRows.map((r) => {
                const chip = chipFor(r);
                const cannotRead = unreadable[keyOf(r)];
                const dim = Boolean(r.quarantine_reason) || r.account_status === 'closed';
                return (
                  <tr
                    key={keyOf(r)}
                    className={`oc-row${dim ? ' oc-row-dim' : ''}`}
                    tabIndex={0}
                    onClick={() => openRow(r)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openRow(r);
                      }
                    }}
                  >
                    <td className="oc-namecell">
                      <div className="oc-name">{r.cust_id || '—'}</div>
                      <div className="oc-sub">
                        {[r.name, `#${r.cust_num}`, r.company].filter(Boolean).join(' · ')}
                      </div>
                    </td>
                    <td className="num">{fmtMoney(r.credit_limit)}</td>
                    <td className="num">{fmtMoney(r.invoice_credit)}</td>
                    <td className={`num${r.credit_include_orders === false ? ' dim' : ''}`}>
                      {fmtMoney(r.order_credit)}
                    </td>
                    <td className={`num${Number(r.reserved_credit) > 0 ? '' : ' dim'}`}>
                      {fmtMoney(r.reserved_credit)}
                    </td>
                    <td className="num">{fmtMoney(r.credit_total)}</td>
                    <td className="num oc-credit-avail">{fmtMoney(r.available_credit)}</td>
                    <td>
                      <span className={`oc-linkchip ${chip.cls}`} title={chip.title}>
                        {chip.label}
                      </span>
                    </td>
                    <td className="oc-muted">
                      {cannotRead ? (
                        <span className="oc-linkchip review">Could not read</span>
                      ) : r._live ? (
                        <span className="oc-linkchip linked">Live</span>
                      ) : (
                        <span title={fmtTime(r.computed_at)}>{fmtAge(r.stale_seconds)}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!initialLoading && rows.length === 0 && !loadError && (
                <tr>
                  <td colSpan={9}>
                    <div className="oc-empty">
                      <p className="oc-empty-title">Nothing matches that</p>
                      <p className="oc-empty-sub">Try a shorter search, or clear the filters.</p>
                      <button type="button" className="oc-btn oc-btn-secondary" onClick={clearFilters}>
                        Clear filters
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="oc-pager">
          <span className="oc-muted oc-pager-note">
            Page {page + 1} of {totalPages} · showing {rows.length.toLocaleString()} of{' '}
            {total.toLocaleString()}
          </span>
          <div className="oc-pager-btns">
            <button
              type="button"
              className="oc-btn oc-btn-secondary"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
            >
              ← Prev
            </button>
            <button
              type="button"
              className="oc-btn oc-btn-secondary"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditPage;
